/**
 * Real-time event service using Server-Sent Events (SSE).
 * Replaces 7 persistent polling loops with 1 SSE connection.
 *
 * Event types:
 * - exception_update: exception created, status changed, resolved
 * - resolution_update: resolution stored, enriched
 * - approval_update: action approved/rejected/escalated
 * - notification: alerts, threshold breaches
 * - agent_update: meta-agent findings
 */

const _env = process.env.REACT_APP_API_URL;
const BASE = _env !== undefined && _env !== null ? _env : 'http://localhost:8000';

type EventHandler = (data: any) => void;

class RealtimeService {
  private abortController: AbortController | null = null;
  private handlers: Map<string, Set<EventHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1s, exponential backoff
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _connected = false;

  connect(token: string): void {
    this.disconnect();
    this.connectWithFetch(token);
  }

  private async connectWithFetch(token: string): Promise<void> {
    const url = `${BASE}/api/v1/sse/events`;

    try {
      this.abortController = new AbortController();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: this.abortController.signal,
      });

      if (response.status === 401 || response.status === 423) {
        console.warn('SSE auth failed, not retrying');
        this._connected = false;
        return;
      }

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      this.reconnectAttempts = 0;
      this._connected = true;
      this.emit('connection_status', { connected: true });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEventType = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEventType) {
              try {
                const data = JSON.parse(line.slice(6));
                this.emit(currentEventType, data);
              } catch {
                // Ignore parse errors on individual events
              }
              currentEventType = '';
            }
            // Keepalive comments (": keepalive") are silently consumed
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return; // Intentional disconnect
        console.warn('SSE stream error:', err);
      }

      // Stream ended -- attempt reconnect
      this._connected = false;
      this.emit('connection_status', { connected: false });
      this.handleReconnect(token);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('SSE connection error:', err);
      this._connected = false;
      this.handleReconnect(token);
    }
  }

  private handleReconnect(token: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SSE max reconnect attempts reached. Falling back to polling.');
      this.emit('connection_lost', {});
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;
    console.log(`SSE reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connectWithFetch(token), delay);
  }

  disconnect(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._connected = false;
    this.reconnectAttempts = 0;
  }

  on(eventType: string, handler: EventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  private emit(eventType: string, data: any): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (err) {
          console.error(`SSE handler error for ${eventType}:`, err);
        }
      });
    }

    // Also emit to wildcard handlers
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach(handler => {
        try {
          handler({ type: eventType, ...data });
        } catch (err) {
          console.error('SSE wildcard handler error:', err);
        }
      });
    }
  }

  get connected(): boolean {
    return this._connected;
  }
}

// Singleton
export const realtimeService = new RealtimeService();
